import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Keyboard } from '@capacitor/keyboard';
import { ThousandSeparatorPipe } from '@inyova/inyova-shared';
import { WithdrawalAllocation, WithdrawalSteps, WithdrawType } from '@inyova/models';
import { toFixed } from '@inyova/utils';
import { Platform } from '@ionic/angular';

import { WithdrawValidator } from '../../validators/withdrawal.validator';

@Component({
  selector: 'app-withdrawal-init',
  templateUrl: './withdrawal-init.component.html',
  styleUrls: ['./withdrawal-init.component.scss'],
  standalone: false
})
export class WithdrawalInitComponent implements OnInit {
  @Input() accountInvested: boolean;
  @Input() withdrawalAllocation: WithdrawalAllocation;
  @Input() currency: string;
  @Input() isMobileApp: boolean;
  @Input() minInvestment: number;
  @Input() parent: FormGroup;
  @Input() withdrawalMinBuffer: number;
  @Output() nextStep: EventEmitter<WithdrawalSteps> = new EventEmitter<WithdrawalSteps>();
  @Output() trackActivity: EventEmitter<string> = new EventEmitter<string>();
  limitedWithdrawal: boolean;
  minAmountText: string;
  selectOptions: { value: string; title: string; desc: string }[] = [
    {
      value: WithdrawType.PARTIAL,
      title: 'ACCOUNT.withdrawalV2.amount.radios.partial.label',
      desc: 'ACCOUNT.withdrawalV2.amount.radios.partial.description'
    },
    {
      value: WithdrawType.FULL,
      title: 'ACCOUNT.withdrawalV2.amount.radios.full.label',
      desc: 'ACCOUNT.withdrawalV2.amount.radios.full.description'
    }
  ];

  withdrawType = WithdrawType;
  get typeControl(): AbstractControl | null {
    return this.parent.get('type');
  }

  get amountControl(): AbstractControl | null {
    return this.parent.get('amount');
  }

  constructor(
    private thousandPipe: ThousandSeparatorPipe,
    private platform: Platform
  ) {}

  ngOnInit() {
    this.minAmountText = this.thousandPipe.transform(this.minInvestment, this.currency);
  }

  onChangeType(type: WithdrawType) {
    this.trackActivity.emit(`[Button] New Withdrawal: Select ${type}`);
    this.typeControl.setValue(type);
    this.typeControl.updateValueAndValidity();

    this.updateValidation(type);
  }

  updateValidation(type: WithdrawType) {
    if (type === WithdrawType.PARTIAL) {
      this.amountControl.setValue('');
      this.amountControl.markAsPristine();
      this.amountControl.setValidators([WithdrawValidator.partialWithdraw(this.withdrawalAllocation.totalAmount)]);
    } else {
      this.amountControl.setValue(type === WithdrawType.SAFE ? this.getMinimumSafeValue() : this.withdrawalAllocation.totalAmount);
      this.amountControl.clearValidators();
    }
    this.amountControl.updateValueAndValidity();
  }

  showMinWarning(value: number): boolean {
    return value < this.withdrawalAllocation.totalAmount && value > this.getMinimumSafeValue();
  }

  getMinimumSafeValue() {
    const minAmount = this.withdrawalAllocation.totalAmount - this.minInvestment;
    return Number(toFixed(minAmount - this.withdrawalAllocation.totalAmount * this.withdrawalMinBuffer, 2));
  }

  onContinue() {
    if (this.parent.invalid) return;
    if (this.isMobileApp && !this.platform.is('desktop') && !this.platform.is('mobileweb')) {
      void Keyboard.hide();
    }

    const withdrawType = this.typeControl.value as WithdrawType;

    let next = WithdrawalSteps.IBAN;

    if ([WithdrawType.PARTIAL, WithdrawType.FULL].includes(withdrawType)) {
      next = WithdrawalSteps.UPDATE_NOTIFICATION;
    }

    this.nextStep.emit(next);
  }
}
